import type React from 'react';

/**
 * The dismiss gestures of a PIN card in the Going PIN sheet (TECH-663). There the drawer is
 * `dismissible={false}` (see the comment on its <Drawer>), so the sheet reads the gestures itself.
 */

/** A downward swipe this long on a PIN card is a dismiss (see the Drawer comment below). */
const SWIPE_DISMISS_PX = 80;

/**
 * Where a dismiss swipe on a PIN card may start, or null. A dismiss sends a request the person
 * cannot take back, so only a deliberate pull counts: not from the input, not with the body
 * scrolled down, and a mouse only from the handle (a mouse drag in the body is text selection).
 */
export function swipeStartOf(e: React.PointerEvent<HTMLElement>): { x: number; y: number } | null {
  const target = e.target instanceof Element ? e.target : null;
  if (!target || target.closest('input')) return null;
  if (e.pointerType === 'mouse' && !target.closest('[data-pin-sheet-handle]')) return null;
  const scroller = e.currentTarget.querySelector('[data-pin-sheet-scroll]');
  if (scroller && scroller.scrollTop !== 0) return null;
  return { x: e.clientX, y: e.clientY };
}

/** A mostly vertical pull down, far enough, with no text selected. */
export function isDismissSwipe(start: { x: number; y: number }, e: React.PointerEvent): boolean {
  const down = e.clientY - start.y;
  const across = Math.abs(e.clientX - start.x);
  if (down < SWIPE_DISMISS_PX || down < 2 * across) return false;
  return (window.getSelection()?.toString() ?? '') === '';
}

/** How far a pull on a held card can move the sheet at most (the rubber band's asymptote). */
const RUBBER_BAND_LIMIT_PX = 120;
/** The rubber band's stiffness: lower gives less under the finger. */
const RUBBER_BAND_CONSTANT = 0.55;

/**
 * How far the sheet gives under a downward pull of `pull` px on a held card (apple-design §9,
 * rubber-banding): it follows the finger with growing resistance and never reaches
 * RUBBER_BAND_LIMIT_PX (about 32px at the 80px dismiss threshold, 69px at a 300px pull), then
 * settles back on release. Continuous feedback for a pull whose release sends a request.
 */
export function rubberBandPx(pull: number): number {
  return (
    (pull * RUBBER_BAND_CONSTANT * RUBBER_BAND_LIMIT_PX) /
    (RUBBER_BAND_LIMIT_PX + RUBBER_BAND_CONSTANT * pull)
  );
}
